import PreviewOverlayBase from "./shared/preview_overlay_base"
import { loadPdf } from "../helpers/load_pdf"

// In-app preview overlay for the document browser. Handles images and PDFs in
// the shared lightbox (see shared/preview_overlay_base): images render into an
// <img>, PDFs page by page into canvases via pdf.js, loaded on demand by
// helpers/load_pdf.js.
//
// PDF pages render in small batches as the user scrolls rather than all at once
// — a plan set can run to hundreds of pages, and rendering every one up front
// froze the tab and held every page bitmap in memory at the same time.
//
// Usage:
//   <div data-controller="document-preview"
//        data-document-preview-kind-value="pdf"           <!-- "image" | "pdf" | "email" -->
//        data-document-preview-url-value="<%= rails_blob_path(blob, disposition: 'inline') %>"
//        data-document-preview-filename-value="<%= blob.filename %>"
//        data-document-preview-download-url-value="<%= rails_blob_path(blob, disposition: 'attachment') %>"
//        data-document-preview-i18n-download-value="<%= t('...') %>"
//        data-document-preview-i18n-close-value="<%= t('...') %>"
//        data-document-preview-i18n-loading-value="<%= t('...') %>"
//        data-document-preview-i18n-error-value="<%= t('...') %>">
//     <button type="button" data-action="document-preview#open">…</button>
//   </div>
const PAGE_BATCH_SIZE = 3

export default class extends PreviewOverlayBase {
  static values = {
    ...PreviewOverlayBase.values,
    kind: String,
    i18nLoading: String,
    i18nError: String,
  }

  renderBody(body) {
    this.body = body

    if (this.kindValue === "pdf") {
      this.renderPdf(body)
    } else if (this.kindValue === "email") {
      this.renderEmail(body)
    } else {
      this.renderImage(body)
    }
  }

  // An .eml is not renderable by the browser, so the server parses it and returns a
  // sanitized fragment. Remote images are withheld until the user asks for them, and
  // asking re-fetches rather than un-hiding client-side: the body renders inside a
  // script-less sandboxed iframe, so there is no code in there to un-hide anything.
  renderEmail(body, { withImages = false } = {}) {
    body.classList.add("flex-col", "items-stretch", "justify-start")

    const status = document.createElement("div")
    status.className = "p-8 text-sm text-gray-500"
    status.textContent = this.i18nLoadingValue || ""
    body.replaceChildren(status)

    const url = withImages ? `${this.urlValue}?load_images=1` : this.urlValue

    // redirect: "manual" so a redirect is never mistaken for the fragment. The endpoint
    // only ever answers with one; a 302 means Pundit denied the file or the session
    // expired while the tab sat open, and following it would dump a full app page —
    // dashboard or sign-in form — into the lightbox via innerHTML. Manual mode turns
    // that into an opaque, non-ok response, so it lands in the error branch instead.
    fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin", redirect: "manual" })
      .then((response) => {
        if (!response.ok) throw new Error(`Unexpected status ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (this.closed) return
        body.innerHTML = html
      })
      .catch(() => this.renderError(body))
  }

  handleBodyClick(event) {
    if (!event.target.closest("[data-preview-load-images]")) return

    event.preventDefault()
    this.renderEmail(this.body, { withImages: true })
  }

  renderImage(body) {
    const img = document.createElement("img")
    img.src = this.urlValue
    img.alt = this.filenameValue || ""
    img.className = "max-h-[85vh] max-w-full object-contain"
    body.appendChild(img)
  }

  renderPdf(body) {
    if (this.closed) return

    body.classList.add("flex-col", "items-start", "p-4")
    this.status = document.createElement("div")
    this.status.className = "p-8 text-sm text-gray-500"
    this.status.textContent = this.i18nLoadingValue || ""
    body.appendChild(this.status)

    loadPdf(this.urlValue)
      .then((pdf) => this.startPagedRender(body, pdf))
      .catch(() => this.renderError(body))
  }

  startPagedRender(body, pdf) {
    if (this.closed) {
      this.destroyPdf(pdf)
      return
    }

    this.pdf = pdf
    this.nextPage = 1
    // Fit page width to the visible body, capped so wide pages stay readable.
    this.targetWidth = Math.min(body.clientWidth - 32, 900) || 900
    this.status.remove()

    // The sentinel sits below the rendered pages; whenever it scrolls into view
    // the next batch renders, which pushes it down again.
    this.sentinel = document.createElement("div")
    this.sentinel.className = "h-px w-full shrink-0"
    body.appendChild(this.sentinel)

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.renderNextBatch(body)
      },
      { root: body },
    )
    this.observer.observe(this.sentinel)

    this.renderNextBatch(body)
  }

  async renderNextBatch(body) {
    if (this.rendering || this.closed || !this.pdf) return
    if (this.nextPage > this.pdf.numPages) return

    this.rendering = true

    try {
      // Keep going while the sentinel is still on screen. The observer alone is
      // not enough: it reports *changes* in intersection, so a batch that does
      // not push the sentinel out of view (a short document, a tall window)
      // would never trigger a second callback and the rest of the pages would
      // silently never render.
      do {
        const lastPage = Math.min(this.nextPage + PAGE_BATCH_SIZE - 1, this.pdf.numPages)
        for (let i = this.nextPage; i <= lastPage; i++) {
          // The overlay can close mid-batch; a render in flight must not append to it.
          if (this.closed) return
          await this.renderPage(body, i)
        }
        this.nextPage = lastPage + 1
      } while (this.nextPage <= this.pdf.numPages && this.sentinelInView(body))

      if (this.nextPage > this.pdf.numPages) this.stopObserving()
    } catch {
      this.renderError(body)
    } finally {
      this.rendering = false
    }
  }

  // Measured directly rather than read from the observer, so the decision uses
  // the layout as it stands after the batch just rendered.
  sentinelInView(body) {
    if (!this.sentinel) return false

    return this.sentinel.getBoundingClientRect().top <= body.getBoundingClientRect().bottom
  }

  async renderPage(body, pageNumber) {
    const page = await this.pdf.getPage(pageNumber)
    if (this.closed) return

    const unscaled = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale: this.targetWidth / unscaled.width })

    const canvas = document.createElement("canvas")
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.className = "mx-auto mb-4 shadow"
    body.insertBefore(canvas, this.sentinel)

    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
  }

  renderError(body) {
    this.stopObserving()
    // Closing the overlay destroys the pdf.js document, which rejects any render
    // still in flight — there is nothing left to show the error in.
    if (this.closed) return

    body.replaceChildren()
    const message = document.createElement("div")
    message.className = "p-8 text-sm text-gray-500"
    message.textContent = this.i18nErrorValue || ""
    body.appendChild(message)
  }

  stopObserving() {
    this.observer?.disconnect()
    this.observer = null
  }

  // The pdf.js we ship has no PDFDocumentProxy#destroy (2.x did) — the document is released through the
  // task that loaded it, which also terminates its worker. destroy() is async; a
  // rejection there has nothing left to report into, the overlay is already gone.
  destroyPdf(pdf) {
    pdf.loadingTask.destroy().catch(() => {})
  }

  teardown() {
    this.stopObserving()
    if (this.pdf) this.destroyPdf(this.pdf)
    this.pdf = null
    this.sentinel = null
    this.status = null
  }
}
